import { verifyEmail, resendEmail, changeEmail, checkEmailVerification } from './api';
import { showLoader, hideLoader } from '../../modules/loaders';
import { keepCodeDigits } from './codeField';

/**
 * Confirming the address, once, for every door that needs it confirmed — after an
 * account is created, and after somebody signs in whose address never was.
 *
 * Four things happen on that screen and every door needs all four: type the code
 * that arrived, ask for another one, correct the address it went to, and notice
 * when it has been confirmed somewhere else — in the mail client, on a phone —
 * without the person coming back to press anything.
 *
 * That last one is why the step checks again whenever the page comes back into
 * view or focus. Somebody who opens the link in their mail app, or on a phone,
 * finds this screen already moved on when they switch back, and nobody sitting on
 * the screen costs a request every few seconds.
 *
 * The markup is shared too: <x-registration.verification> draws it, so the ids
 * below are the same wherever the step appears. What a door does once the address
 * is confirmed is its own business and arrives as `onConfirmed`.
 */

const IDS = {
    section: '#verificationCodeSection',
    code: '#verification_code',
    verifyButton: '#verifyCodeButton',
    resendButton: '#resendVerificationCode',
    addressShown: '#verificationEmailShown',
    newAddress: '#new_email',
    changeLink: '#changeEmailLink',
    changeButton: '#changeEmailButton',
    changeFields: '#changeEmailFields',
    changeError: '#changeEmailError'
};

export function verificationStep({ form, onConfirmed, onInvalidCode, translate } = {}) {
    let watching = false;
    let checking = false;

    // The flow says which form to hide; core does not know any door's markup.
    const formSelector = form;

    const say = translate || ((key, fallback) => fallback);

    function userId() {
        return $(IDS.verifyButton).attr('data-user-id');
    }

    function approved() {
        return $(IDS.verifyButton).attr('data-user-approved') === 'true';
    }

    function stopWatching() {
        watching = false;
        document.removeEventListener('visibilitychange', checkOnReturn);
        window.removeEventListener('focus', checkOnReturn);
    }

    function confirmed() {
        stopWatching();

        if (onConfirmed) {
            onConfirmed(approved());
        }
    }

    function invalid() {
        if (onInvalidCode) {
            onInvalidCode();
        }
    }

    // Coming back from another tab fires both events, so one check at a time.
    function checkOnReturn() {
        if (! watching || checking || document.visibilityState !== 'visible') {
            return;
        }

        const id = userId();

        if (! id) {
            stopWatching();
            return;
        }

        checking = true;

        checkEmailVerification(id)
            .done((response) => {
                if (! watching || ! response || ! response.email_verified) {
                    return;
                }

                if (typeof response.is_user_approved !== 'undefined') {
                    $(IDS.verifyButton).attr('data-user-approved', response.is_user_approved ? 'true' : 'false');
                }

                confirmed();
            })
            .fail((xhr) => {
                console.error('Error checking email verification status:', xhr?.responseText || xhr);
            })
            .always(() => {
                checking = false;
            });
    }

    function submitCode(event) {
        event.preventDefault();

        const code = $(IDS.code).val();

        if (! code) {
            $(IDS.code).focus();
            return;
        }

        showLoader();

        verifyEmail({
            id: userId(),
            verification_code: code,
            _token: $('meta[name="csrf-token"]').attr('content') || ''
        })
            .done((response) => {
                hideLoader();

                if (response && response.success) {
                    confirmed();
                } else {
                    invalid();
                }
            })
            .fail(() => {
                hideLoader();
                invalid();
            });
    }

    function resend(event) {
        event.preventDefault();

        const $button = $(event.currentTarget);

        $button.prop('disabled', true);

        resendEmail({ id: userId(), _token: $('meta[name="csrf-token"]').attr('content') || '' })
            .always(() => $button.prop('disabled', false));
    }

    function correctAddress(event) {
        event.preventDefault();

        const $input = $(IDS.newAddress);
        const $error = $(IDS.changeError);
        const address = ($input.val() || '').trim();

        $error.hide().text('');

        if (! address) {
            return;
        }

        showLoader();

        changeEmail(address)
            .done((data) => {
                // Only the server's word that it changed counts: a signed-out session
                // is answered with a page, not a refusal, and that is no change.
                if (! data || ! data.success) {
                    $error.text(say('generic_error', 'An error occurred. Please try again.')).show();
                    return;
                }

                showAddress(data.email);
                $(IDS.changeFields).slideUp();
            })
            .fail((jqXHR) => {
                const payload = jqXHR.responseJSON || {};
                const message = payload.errors?.email
                    ? payload.errors.email[0]
                    : (payload.message || say('invalid_email', 'Enter a valid email address.'));

                $error.text(message).show();
                $input.addClass('is-invalid');
            })
            .always(() => hideLoader());
    }

    /** Where the code was sent, said back so a typo is visible before waiting for it. */
    function showAddress(address) {
        $(IDS.addressShown).text(address || '');
        $(IDS.newAddress).val(address || '');
    }

    function scrollIntoView() {
        const $section = $(IDS.section);

        if (! $section.length) {
            return;
        }

        $('html, body').animate({ scrollTop: $section.offset().top - 300 }, 800);
    }

    function startWatching() {
        stopWatching();
        watching = true;
        document.addEventListener('visibilitychange', checkOnReturn);
        window.addEventListener('focus', checkOnReturn);
    }

    /**
     * Handing the page over to this step, which happens from two directions: an
     * account was just created, or somebody signed in and the address turned out
     * never to have been confirmed. Both arrive with an id and an address and both
     * want the same screen, so only the sentence on top of it is the door's.
     */
    function begin({ userId, approved = false, address, onReady } = {}) {
        const $section = $(IDS.section);

        if (! $section.length || ! userId) {
            // Nothing to draw, or nobody to draw it for: the standalone page has
            // both, and it is a better answer than a dead end.
            window.location.href = '/email/verify';
            return;
        }

        $(IDS.verifyButton)
            .attr('data-user-id', userId)
            .attr('data-user-approved', approved ? 'true' : 'false');
        $(IDS.resendButton).attr('data-user-id', userId);

        showAddress(address);

        $(formSelector).hide();
        $section.fadeIn(() => {
            scrollIntoView();

            if (onReady) {
                onReady();
            }
        });

        startWatching();
    }

    keepCodeDigits();

    // Delegated: the section is revealed after the form is submitted, so these
    // elements are not in the document when a flow starts.
    $(document)
        .on('click', IDS.verifyButton, submitCode)
        .on('click', IDS.resendButton, resend)
        .on('click', IDS.changeButton, correctAddress)
        .on('click', IDS.changeLink, (event) => {
            event.preventDefault();
            $(IDS.changeFields).slideToggle();
        });

    // begin does the whole handover, so the steps it is made of stay private. A flow
    // only ever needs to start the step and, on a reset, to stop it watching.
    return {
        begin,
        stopWatching
    };
}
